/* Pickup service — shipper address → nearest ELC stations → the `pickup`
   the quote POST carries. Hook + panel (2026-11-03), extracted for the
   customer portal from the inline flow in newquote.js; the staff form still
   runs its own copy of this logic (follow-up: move it onto this module).

   `api` picks the endpoint family: staff '/api/elcapp/' (admin-gated,
   station-scoped candidates) or portal '/api/elcapp/portal/' (login-gated,
   every station). The caller owns Origin: `find()` resolves to the
   candidates, the caller decides whether the closest one sets Origin. */
import { useState, useRef, C } from '../shared/ui.js';

const newToken = () => (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now());
const ZIP_RE   = /^(\d{5})(?:-\d{4})?$/;

// Crude country sniff from the suggestion's secondary_text trailer —
// autocomplete already filters to US/CA/MX. Default US.
const regionFromSuggestion = (s) => {
  const tail = (s.secondary_text || '').trim().toLowerCase();
  if (tail.endsWith('canada')) return 'CA';
  if (tail.endsWith('mexico')) return 'MX';
  return 'US';
};

const usePickup = ({ api = '/api/elcapp/' } = {}) => {
  const [enabled,      setEnabled]      = useState(false);
  const [address,      setAddress]      = useState('');
  const [suggestions,  setSuggestions]  = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  // Resolved city/state for a ZIP-only input (the field keeps the bare ZIP).
  const [zipInfo,      setZipInfo]      = useState(null);
  // Google bills type → pick → validate as one session on this token; rotated
  // after every validate call.
  const [sessionToken, setSessionToken] = useState(newToken);
  const [regionCode,   setRegionCode]   = useState('US');
  // null = unknown (non-US or unverified); true = residential; false = business.
  const [residential,  setResidential]  = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [candidates,   setCandidates]   = useState([]);
  const [distInfo,     setDistInfo]     = useState(null);   // {source, basis}
  const [selected,     setSelected]     = useState('');
  // Structured shipper parts — the GlobalTranz pickup quote needs them.
  const [shipper,      setShipper]      = useState({ zip: '', city: '', state: '' });
  const debounce = useRef(null);

  // Any address change drops a previously selected station so a stale
  // station / shipper can't ride along on Get Quote.
  const invalidate = () => {
    setSelected(''); setShipper({ zip: '', city: '', state: '' });
    setCandidates([]); setDistInfo(null); setZipInfo(null); setError(null);
  };

  const onAddressChange = (value) => {
    setAddress(value);
    setResidential(null);
    invalidate();
    if (debounce.current) clearTimeout(debounce.current);
    const q = value.trim();
    if (!q) { setSuggestions([]); setShowDropdown(false); return; }
    // ZIP path: resolve the city from our own postalcode table — no Google call.
    const zipM = q.match(ZIP_RE);
    if (zipM) {
      debounce.current = setTimeout(async () => {
        try {
          const d = await fetch(`${api}zip-lookup?zip=${encodeURIComponent(zipM[1])}`).then(r => r.json());
          if (d && d.found) {
            setSuggestions([{ is_zip: true, zip: d.postal_code, city: d.city, state: d.state_code,
                              place_id: `zip:${d.postal_code}`,
                              primary_text: `${d.city}, ${d.state_code}`, secondary_text: d.postal_code }]);
            setShowDropdown(true);
          } else { setSuggestions([]); setShowDropdown(false); }
        } catch (e) { /* best-effort */ }
      }, 200);
      return;
    }
    debounce.current = setTimeout(async () => {
      try {
        const r = await fetch(`${api}address-autocomplete?query=${encodeURIComponent(q)}&session_token=${encodeURIComponent(sessionToken)}`);
        if (!r.ok) return;
        const d = await r.json();
        setSuggestions(d.suggestions || []);
        setShowDropdown((d.suggestions || []).length > 0);
      } catch (e) { /* autocomplete is best-effort; Find still works on typed text */ }
    }, 300);
  };

  const pickSuggestion = (s) => {
    invalidate();
    setShowDropdown(false); setSuggestions([]);
    if (s.is_zip) {
      // Keep the BARE ZIP so Find uses the local ZIP-centroid path.
      setAddress(s.zip); setRegionCode('US');
      setShipper({ zip: s.zip, city: s.city, state: s.state });
      setZipInfo({ city: s.city, state: s.state });
      return;
    }
    setAddress([s.primary_text, s.secondary_text].filter(Boolean).join(', '));
    setRegionCode(regionFromSuggestion(s));
  };

  // Validate (best-effort, US residential tag) → distance → candidates.
  // Resolves to the candidate list; [] on failure (error state set).
  const find = async () => {
    const addr = address.trim();
    if (!addr) return [];
    let distAddr = addr;
    setLoading(true); setError(null); setCandidates([]); setDistInfo(null);
    setResidential(null); setShipper({ zip: '', city: '', state: '' }); setSelected('');
    try {
      try {
        const vr = await fetch(`${api}address-validate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ address: addr, region_code: regionCode, session_token: sessionToken }),
        });
        if (vr.ok) {
          const v = await vr.json();
          if (typeof v.is_residential === 'boolean') setResidential(v.is_residential);
          if (v.formatted_address) {
            setAddress(v.formatted_address);
            // Geocode what Google corrected the text to; a bare ZIP keeps the local path.
            if (!ZIP_RE.test(addr)) distAddr = v.formatted_address;
          }
          setShipper({ zip: v.postal_code || '', city: v.city || '', state: v.state || '' });
        }
      } catch (_) { /* validate is best-effort */ }
      setSessionToken(newToken());

      const r = await fetch(`${api}distance`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shipper_address: distAddr }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || `Lookup failed (HTTP ${r.status})`); return []; }
      const cands = d.candidates || [];
      setCandidates(cands);
      setDistInfo({ source: d.source, basis: d.distance_basis });
      // A ZIP-only input: the distance answer resolved city/state from our table.
      if (d.shipper && d.shipper.zip) {
        setShipper({ zip: d.shipper.zip || '', city: d.shipper.city || '', state: d.shipper.state || '' });
      }
      if (cands.length) setSelected(cands[0].station_code);
      return cands;
    } catch (e) {
      setError('Network error — please try again');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const selectedCandidate = candidates.find(c => c.station_code === selected) || null;

  // The `pickup` the quote POST carries — null when off or no station picked.
  const payload = () => (enabled && selected) ? {
    enabled: true, station_code: selected,
    shipper: { ...shipper, address: address.trim() },
    miles: selectedCandidate ? selectedCandidate.distance_mi : null,
    residential: residential === true,
  } : null;

  return { enabled, setEnabled, address, suggestions, showDropdown, setShowDropdown, zipInfo,
           residential, loading, error, candidates, distInfo, selected, setSelected, shipper,
           selectedCandidate, onAddressChange, pickSuggestion, find, payload };
};

// The pickup card. `onFound(cands)` runs after Find; `onSelect(cand)` after a
// click on a station card — the caller sets Origin from either. `children`
// renders under the cards (the Origin conflict prompt).
const PickupPanel = ({ p, onFound, onSelect, highlight = false, children, style = {} }) => (
  <div className="card" style={{ padding: 22, ...(highlight ? { borderColor: '#d97706', boxShadow: '0 0 0 3px rgba(217,119,6,.18)' } : {}), ...style }}>
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', fontSize: 13, color: C.text }}>
      <input type="checkbox" checked={p.enabled} onChange={e => p.setEnabled(e.target.checked)} style={{ width: 'auto' }} />
      <span>Need pickup service? <span style={{ color: C.textD, fontSize: 11 }}>(ELC arranges a truck from the shipper's address)</span></span>
    </label>
    {p.enabled && (
      <>
        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'end' }}>
          <div style={{ position: 'relative' }}>
            <span className="lbl">Shipper address</span>
            <input type="text" value={p.address}
                   onChange={e => p.onAddressChange(e.target.value)}
                   onFocus={() => p.suggestions.length > 0 && p.setShowDropdown(true)}
                   onBlur={() => setTimeout(() => p.setShowDropdown(false), 180)}
                   placeholder="Street address or ZIP…"
                   autoComplete="off" />
            {p.showDropdown && p.suggestions.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 4, background: '#fff',
                            border: `1px solid ${C.brd}`, borderRadius: 8, boxShadow: '0 10px 24px rgba(0,0,0,.15)', maxHeight: 280, overflowY: 'auto' }}>
                {p.suggestions.map((s, idx) => (
                  <div key={s.place_id || idx}
                       onMouseDown={(e) => { e.preventDefault(); p.pickSuggestion(s); }}
                       style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: idx < p.suggestions.length - 1 ? `1px solid ${C.brd}` : 'none' }}
                       onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(37,99,235,.08)'}
                       onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{s.primary_text}</div>
                    {s.secondary_text && <div style={{ fontSize: 11, color: C.textM, marginTop: 2 }}>{s.secondary_text}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="btn bb" disabled={!p.address.trim() || p.loading}
                  onClick={() => p.find().then(c => onFound && onFound(c))}>
            {p.loading ? 'Finding…' : 'Find nearest station'}
          </button>
        </div>
        {p.zipInfo && (
          <div style={{ marginTop: 8, fontSize: 11, color: '#15803d' }}>
            ✓ {p.zipInfo.city}, {p.zipInfo.state} — click <b>Find nearest station</b>
          </div>
        )}
        {p.residential === true && (
          <div style={{ marginTop: 8, fontSize: 11, color: '#92400e', background: 'rgba(217,119,6,.08)', border: '1px solid rgba(217,119,6,.25)', borderRadius: 6, padding: '4px 10px', display: 'inline-block' }}>
            Residential address
          </div>
        )}
        {p.error && (
          <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(239,68,68,.06)', border: '1px solid rgba(239,68,68,.25)', borderRadius: 6, fontSize: 12, color: '#b91c1c' }}>
            {p.error}
          </div>
        )}
        {p.candidates.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, color: C.textD, marginBottom: 8 }}>
              Suggested stations — closest first. Click to set Origin.
              {p.distInfo && p.distInfo.source === 'zip_centroid' && (
                <span> · distance from ZIP center{p.distInfo.basis === 'straight_line' ? ' (approx. straight-line)' : ''}</span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${p.candidates.length}, 1fr)`, gap: 10 }}>
              {p.candidates.map(c => {
                const isSel = c.station_code === p.selected;
                return (
                  <div key={c.station_code}
                       onClick={() => { p.setSelected(c.station_code); onSelect && onSelect(c); }}
                       style={{ cursor: 'pointer', padding: '12px 14px', borderRadius: 8, border: `1px solid ${isSel ? C.blue : C.brd}`,
                                background: isSel ? 'rgba(37,99,235,.08)' : 'transparent', transition: 'all 120ms ease' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span className="mono bc" style={{ fontSize: 14, fontWeight: 700, color: C.blue }}>{c.airport_code}</span>
                      <span style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>{c.airport_name || c.city}</span>
                    </div>
                    <div style={{ fontSize: 11, color: C.textM }}>
                      <b>{c.distance_mi}</b> mi{c.duration_min != null ? <> · ~<b>{c.duration_min}</b> min drive</> : ''}
                    </div>
                  </div>
                );
              })}
            </div>
            {children}
          </div>
        )}
      </>
    )}
  </div>
);

export { usePickup, PickupPanel };
